import { Limits } from '@studio/models/control/preset';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { StudioDeviceControlPresetGroup } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { isEmpty, toString } from '@tryiris-ai/js-types';
import type { UpdateGroup } from './types.js';
import { locateProxy } from '#~/agent/index.js';
import { StudioError } from '#~/error/index.js';

/**
 * Update an existing preset group.
 *
 * @param deviceId - The ID of the device to which this group belongs.
 * @param groupId - The ID of the group to be updated.
 * @param updates - Changes to be made to the group.
 * @param token - The token.
 */
export const updateGroup: UpdateGroup = async (deviceId, groupId, updates, token) => {
  try {
    const proxy = locateProxy(deviceId);

    if (!proxy) {
      throw new Error('UP-481');
    }

    // Define mutation
    const mutation: DeepPartial<StudioDeviceControlPresetGroup> = {};

    const name = toString(updates.name);

    if (name) {
      mutation.name = name.slice(0, Limits.maxNameLength);
    }

    // Are there any changes to be made?
    if (isEmpty(mutation)) {
      return;
    }

    await proxy.mutate(
      {
        control: {
          studio: {
            device: {
              [deviceId]: {
                control: {
                  presets: {
                    groups: {
                      [groupId]: mutation
                    }
                  }
                }
              }
            }
          }
        }
      },
      {}
    );

    await proxy.save(token);
  } catch (cause) {
    throw new StudioError('Unable to update preset.', { cause });
  }
};
